/**
 * Локали трекера. Строки компонентов дизайн-системы лежат в основании: свои
 * переводы приложения их перекрывают, а новый ключ пакета не покажется на
 * экране сырым `common.*` до правки локалей здесь.
 *
 * Выбранный язык уходит на бэкенд заголовком `user-locale` (см. `api/http`),
 * в теле запросов его не бывает.
 */
import { createI18n } from 'vue-i18n'
import { dsLocaleMessages } from '@vinprop/design-system'

import en from './locales/en'
import ru from './locales/ru'
import vn from './locales/vn'

export const SUPPORTED_LOCALES = ['ru', 'en', 'vn'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'ru'

const STORAGE_KEY = 'tracker-locale'

type AnyRecord = Record<string, unknown>

/** Слияние вглубь: пакет кладётся в основание, приложение перекрывает. */
const deepMerge = (base: AnyRecord, override: AnyRecord): AnyRecord => {
  const out: AnyRecord = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const prev = out[key]
    const bothObjects =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      prev !== null &&
      typeof prev === 'object' &&
      !Array.isArray(prev)

    out[key] = bothObjects ? deepMerge(prev as AnyRecord, value as AnyRecord) : value
  }

  return out
}

/** Схема сообщений — русская локаль: она полная, остальные сверяются с ней. */
export type MessageSchema = typeof ru

const appMessages: Record<Locale, AnyRecord> = { ru, en, vn }

export const messages = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => [
    locale,
    deepMerge(dsLocaleMessages[locale] as unknown as AnyRecord, appMessages[locale]),
  ]),
) as Record<Locale, MessageSchema>

const isLocale = (value: string | null): value is Locale =>
  !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value)

export const readStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  const stored = window.localStorage.getItem(STORAGE_KEY)

  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

export const i18n = createI18n<[MessageSchema], Locale, false>({
  legacy: false,
  locale: readStoredLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages,
})

/** Текущая локаль для заголовка `user-locale` и для смены языка из UI. */
export const currentLocale = (): Locale => i18n.global.locale.value

export const setLocale = (locale: Locale) => {
  i18n.global.locale.value = locale

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale)
    document.documentElement.setAttribute('lang', locale)
  }
}
